import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { useHotkeys } from 'react-hotkeys-hook';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Modal, Paper } from '@mui/material';
import * as yup from 'yup';

import {
  DualButton,
  HorizontalStepper,
  ImagePreviewCard,
  RhfTextField,
  AutocompleteDropdown
} from '@components';
import { colors } from '@constants/colors';
import { translate } from '@utils/locale';
import { EmptySearchState } from '@assets/icons';
import {
  IMAGE_FORMATS,
  LONG_FIELD_MAX_LENGTH,
  TEXT_FIELD_MAX_LENGTH
} from '@constants/common';
import { generateFileUrl, getFileNameFromUrl } from '@utils/common';
import { ToastTypes } from '@type/toast';
import { OptionsLOVType } from '@components/auto-complete/types';
import { showGenericErrorToast } from '@utils/error';
import displayToast from '@components/toast/customToast';
import ImageUploader from '@containers/ImageUploader/ImageUploader';

import {
  AddInstituitionalInvestorModalPropsType,
  CreateInstituitionalInvestorFieldValues,
  ExternalInvestorType,
  UpdateInvestorRes
} from '../types';
import { FIELD_TO_TAB_MAPPER_INSTITUITIONAL_INVESTOR } from '../constants';
import {
  useGetInvestorQuery,
  useCreateInvestorMutation,
  useUpdateInvestorMutation
} from '../api';
import { formatCreateUpdateInstitutionalInvestorPayload } from '../services/investors';

const AddInstituitionalInvestorModal: FC<
  AddInstituitionalInvestorModalPropsType
> = ({
  handleCloseModal,
  isOpen,
  setModalOpen,
  memberDetails,
  roundId,
  maxOrder,
  setIsDeleteModalOpen,
  disableChooseInvestor,
  resetInstitutionalOffset
}) => {
  const steps = ['Instituition Info', 'Upload Photos'];

  const { companyId } = useParams();

  const [activeStep, setActiveStep] = useState<number>(0);
  const [deleteClickedFile, setDeleteClickedFile] = useState<string>('');

  const [createInvestor, { isLoading }] = useCreateInvestorMutation();
  const [updateInvestor, { isLoading: isInvestorUpdating }] =
    useUpdateInvestorMutation();
  const { data: investorsList } = useGetInvestorQuery({
    companyId: companyId ?? '',
    payload: { roundId }
  });

  const invertorsUserIds = investorsList?.data?.map(val => val.user_id);

  const { data: wholeInvestorsList } = useGetInvestorQuery({
    payload: {}
  });

  const companyInvestorList = wholeInvestorsList?.data
    ?.filter(val => val.type === ExternalInvestorType.InstitutionalInvestor)
    .filter(val => !invertorsUserIds?.includes(val.user_id));
  const companyInvestorNamesList =
    companyInvestorList?.map(val => ({ id: val.id ?? '', name: val.name })) ??
    [];

  const schema = yup.object().shape({
    instituitonalInvestorId: disableChooseInvestor
      ? yup.string()
      : yup.string().required(translate('requiredInput')),
    instituitonalInvestorName: yup
      .string()
      .required(translate('company.placeholders.institutionalInvestorName'))
      .test(
        'required',
        translate('company.placeholders.teamMemberBio'),
        val => !!val.trimEnd()
      ),
    instituitonalInvestorURL: yup
      .string()
      .test(
        'required',
        translate('company.placeholders.institutionalInvestorUrl'),
        val => !!val?.trimEnd()
      )
      .url(translate('company.placeholders.institutionalInvestorUrl'))
      .required(translate('company.placeholders.institutionalInvestorUrl')),
    instituitonalInvestorLogo: yup
      .mixed()
      .test(
        'requiredValidation',
        translate('company.placeholders.institutionalInvestorLogo'),
        (value: { file: File }) => {
          return !!value?.file;
        }
      ),
    instituitonalInvestorDescription: yup.string().nullable()
  });

  const {
    control,
    getValues,
    setValue,
    trigger,
    reset,
    clearErrors,
    formState: { errors },
    handleSubmit,
    setError
  } = useForm<CreateInstituitionalInvestorFieldValues>({
    resolver: yupResolver(
      schema
    ) as never as Resolver<CreateInstituitionalInvestorFieldValues>,
    mode: 'onSubmit',
    reValidateMode: 'onChange'
  });

  const onSubmit = async (data: CreateInstituitionalInvestorFieldValues) => {
    try {
      if (memberDetails?.id) {
        const response = await updateInvestor({
          companyId: companyId ?? '',
          payload: formatCreateUpdateInstitutionalInvestorPayload(
            data,
            roundId
          ),
          investorId: memberDetails.id
        }).unwrap();
        handleCreateUpdateRes(response);
      } else {
        const response = await createInvestor({
          companyId: companyId ?? '',
          payload: formatCreateUpdateInstitutionalInvestorPayload(
            { ...data, instituitionalInvestorOrder: maxOrder + 1 },
            roundId
          )
        }).unwrap();
        handleCreateUpdateRes(response);
      }
    } catch (err) {
      showGenericErrorToast(err);
    }
  };

  const typedInvestorName = useWatch({
    control: control,
    name: 'instituitonalInvestorName'
  });

  const typedInvesterExists = companyInvestorNamesList?.find(
    val => val.name === typedInvestorName
  );

  const showFileUploader =
    deleteClickedFile === 'instituitonalInvestorLogo' ||
    !getValues('instituitonalInvestorLogo.path');

  const getImageUrl = () =>
    generateFileUrl(
      getValues('instituitonalInvestorLogo.path'),
      getValues('instituitonalInvestorLogo.file')
    );

  const getFileName = () =>
    getValues('instituitonalInvestorLogo.file')?.name || '';

  const deleteFile = (value: string) => {
    setDeleteClickedFile(value);
    setValue(value as never as keyof CreateInstituitionalInvestorFieldValues, {
      file: undefined,
      path: ''
    });
  };

  const onFileUploadSuccess = (file: File, path: string) => {
    setValue('instituitonalInvestorLogo', {
      file: file,
      path: path
    });
    clearErrors('instituitonalInvestorLogo');
    setDeleteClickedFile('');
  };

  const onClickCancel = () => {
    reset({});
    handleCloseModal();
  };

  const onNextClick = async (currentStep: number) => {
    const isValid = await trigger(
      FIELD_TO_TAB_MAPPER_INSTITUITIONAL_INVESTOR[
        currentStep as keyof typeof FIELD_TO_TAB_MAPPER_INSTITUITIONAL_INVESTOR
      ] as never as keyof CreateInstituitionalInvestorFieldValues
    );

    if (isValid) {
      if (currentStep === -1) {
        const selectedMemberDetails = companyInvestorList?.find(
          item => item.id === getValues('instituitonalInvestorId')
        );
        if (selectedMemberDetails)
          onSubmit({
            instituitonalInvestorName: selectedMemberDetails.name?.trim(),
            instituitonalInvestorURL: selectedMemberDetails.url ?? '',
            instituitionalInvestorDesignation:
              selectedMemberDetails.designation || '',
            instituitonalInvestorLogo: {
              file: selectedMemberDetails.logo_path
                ? new File(
                    [],
                    getFileNameFromUrl(selectedMemberDetails.logo_path)
                  )
                : undefined,
              path: selectedMemberDetails.logo_path
            },
            instituitonalInvestorDescription:
              selectedMemberDetails.description?.trim() || null,
            instituitonalInvestorId: selectedMemberDetails.id,
            instituitionalInvestorUserId: selectedMemberDetails.user_id ?? '',
            instituitionalInvestorVisibleInDeal:
              selectedMemberDetails.is_visible_in_deal ?? false,
            instituitionalInvestorCreatedAt:
              selectedMemberDetails.created_at ?? '',
            instituitionalInvestorOrder: selectedMemberDetails.order ?? 0
          });
        else
          setError('instituitonalInvestorId', {
            message: 'Required',
            type: 'required'
          });
        return;
      }
      if (currentStep < steps.length - 1) setActiveStep(currentStep + 1);
      else onSubmit(getValues());
    } else {
      setActiveStep(currentStep);
    }
  };

  useHotkeys('enter', () => onNextClick(activeStep), { enabled: isOpen });

  const handleStepClick = async (index: number) => {
    const prevStep = activeStep;
    const newStep = index;
    if (prevStep > newStep) setActiveStep(newStep);
    else {
      const isValid = await trigger(
        FIELD_TO_TAB_MAPPER_INSTITUITIONAL_INVESTOR[
          prevStep as keyof typeof FIELD_TO_TAB_MAPPER_INSTITUITIONAL_INVESTOR
        ] as never as keyof CreateInstituitionalInvestorFieldValues
      );
      if (!isValid || prevStep === newStep) return;
      setActiveStep(newStep);
    }
  };

  const handleCreateUpdateRes = (response: UpdateInvestorRes) => {
    if (response.status === 'ok') {
      setModalOpen(false);
      reset({});
      resetInstitutionalOffset();
      handleCloseModal();
    } else {
      displayToast(
        ToastTypes.ERROR,
        response.error?.details?.[0] ?? 'Failed to Add Institutional Investor'
      );
    }
  };

  const onClickPrimaryButton = () => {
    onNextClick(activeStep);
  };

  const onClickSecondaryButton = () => {
    if (activeStep > 0) setActiveStep(activeStep - 1);
    else onClickCancel();
  };

  const AddInvestorButton = () => {
    setValue('instituitonalInvestorId', ' ');
    setActiveStep(0);
  };

  const onDeleteInvestorClick = () => {
    setModalOpen(false);
    setIsDeleteModalOpen(true);
  };

  const onChangeFieldValue = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    name: keyof CreateInstituitionalInvestorFieldValues
  ) => {
    setValue(name, event.target.value);
    clearErrors(name);
  };

  const emptyState =
    typedInvestorName && !typedInvesterExists ? (
      <div className="flex flex-col gap-2 justify-center w-full h-[300px]">
        <div className="flex justify-center">
          <EmptySearchState />
        </div>
        <div className="text-center">
          {translate('company.investorsTable.emptyState', {
            searchTerm: typedInvestorName
          })}
        </div>
        <div className="flex gap-4 justify-center">
          <Button variant="contained" onClick={AddInvestorButton}>
            {translate('company.investorsTable.createInstInvestor')}
          </Button>
        </div>
      </div>
    ) : (
      <></>
    );

  const handleInvestorNameChange = (_: ChangeEvent<{}>, value: string) => {
    clearErrors('instituitonalInvestorName');
    setValue('instituitonalInvestorName', value);
    clearErrors('instituitonalInvestorId');
  };

  const onInvestorSelect = (selectedItem: OptionsLOVType) => {
    clearErrors('instituitonalInvestorId');
    setValue('instituitonalInvestorId', selectedItem?.id ?? '');
  };

  const renderForm = () => {
    switch (activeStep) {
      case -1:
        return (
          <>
            <AutocompleteDropdown
              control={control}
              name={'instituitonalInvestorId'}
              placeholder={translate('company.addInstituitionalInvestor.name')}
              options={companyInvestorNamesList}
              errors={errors}
              wrapperClass="pt-6 mr-6 w-full"
              handleInputChange={handleInvestorNameChange}
              onChangeHandler={onInvestorSelect}
              selectedItem={companyInvestorNamesList.find(
                value =>
                  value.id === (getValues('instituitonalInvestorId') ?? '')
              )}
              inputValue={typedInvestorName}
              clearOnBlur={false}
              maxLength={TEXT_FIELD_MAX_LENGTH}
              showCharacterLimit
              autoFocusField={true}
            />
            {emptyState}
          </>
        );
      case 0:
        return (
          <>
            <RhfTextField
              name="instituitonalInvestorName"
              label={translate('company.addInstituitionalInvestor.name')}
              placeholder={translate(
                'company.placeholders.institutionalInvestorName'
              )}
              control={control}
              errors={errors}
              wrapperClass="mb-5 w-full"
              onChange={event =>
                onChangeFieldValue(event, 'instituitonalInvestorName')
              }
              maxLength={TEXT_FIELD_MAX_LENGTH}
              showCharacterLimit
              autoFocusField={true}
            />
            <RhfTextField
              name="instituitonalInvestorURL"
              label={translate('company.addInstituitionalInvestor.url')}
              placeholder={translate(
                'company.placeholders.institutionalInvestorUrl'
              )}
              control={control}
              errors={errors}
              wrapperClass="mb-5 w-full"
              onChange={event =>
                onChangeFieldValue(event, 'instituitonalInvestorURL')
              }
            />
            <RhfTextField
              name="instituitonalInvestorDescription"
              label={translate('company.addInstituitionalInvestor.description')}
              placeholder={translate(
                'company.placeholders.institutionalInvestorDescription'
              )}
              control={control}
              multiline={true}
              errors={errors}
              maxLength={LONG_FIELD_MAX_LENGTH}
              rows={6}
              wrapperClass="mb-5 w-full"
              onChange={event =>
                onChangeFieldValue(event, 'instituitonalInvestorDescription')
              }
              showCharacterLimit
            />
          </>
        );

      case 1:
        return (
          <>
            <div className="flex flex-row items-center pb-2 text-base font-normal text-black/60">
              {translate('company.addInstituitionalInvestor.logoDescription')}
            </div>
            <div className="flex flex-row items-center pb-2 text-base font-normal text-black/60">
              {translate('company.addInstituitionalInvestor.logo')}
            </div>
            {showFileUploader ? (
              <ImageUploader
                name="instituitonalInvestorLogo"
                enableCrop={true}
                sizeInMb={3}
                supportedFormats={IMAGE_FORMATS}
                fileUploadSuccessHandler={onFileUploadSuccess}
                deleteFileHandler={() =>
                  deleteFile('instituitonalInvestorLogo')
                }
                errors={errors}
                previousFile={getValues('instituitonalInvestorLogo.file')}
                aspectRatio={1}
                showPreview={true}
                customUploadText={translate('company.uploadImageText', {
                  aspectRatio: '1:1'
                })}
              />
            ) : (
              <ImagePreviewCard
                name="instituitonalInvestorLogo"
                isEdit={true}
                handleDeleteClick={() =>
                  deleteFile('instituitonalInvestorLogo')
                }
                imageUrl={getImageUrl()}
                fileName={getFileName()}
              />
            )}
          </>
        );
      default:
        return <></>;
    }
  };

  useEffect(() => {
    if (memberDetails?.id) {
      reset({
        instituitonalInvestorName: memberDetails.name,
        instituitonalInvestorURL: memberDetails.url,
        instituitonalInvestorLogo: {
          file: memberDetails.logoPath
            ? new File([], getFileNameFromUrl(memberDetails.logoPath))
            : undefined,
          path: memberDetails.logoPath
        },
        instituitonalInvestorDescription: memberDetails.description,
        instituitionalInvestorOrder: memberDetails.order ?? 0
      });
    }
  }, [memberDetails, isOpen]);

  useEffect(() => {
    if (disableChooseInvestor) setActiveStep(0);
    else setActiveStep(-1);
    setDeleteClickedFile('');
    reset({});
  }, [isOpen]);

  return (
    <Modal open={isOpen} onClose={handleCloseModal}>
      <div className="flex flex-col justify-center items-center h-full bg-black/60">
        <Paper
          style={{
            paddingTop: 20,
            paddingBottom: 20,
            width: 450,
            maxHeight: 700,
            overflow: 'auto'
          }}>
          <div className="flex flex-col bg-white">
            <div className="px-5 pb-5 mb-5 text-lg font-medium leading-6 border-b border-gray-200 ">
              {activeStep === -1
                ? translate('company.addInstituitionalInvestor.chooseInvestor')
                : `${memberDetails?.id ? 'Edit' : 'Add'} ${translate(
                    'company.addInstituitionalInvestor.title'
                  )}`}
            </div>
            {activeStep !== -1 && (
              <div className="px-5">
                <HorizontalStepper
                  activeStep={activeStep}
                  handleStep={handleStepClick}
                  steps={steps}
                />
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="p-5 w-full min-h-[250px]">{renderForm()}</div>
              <div className="flex justify-between px-5 pt-5 w-full text-lg font-medium leading-6 border-t border-gray-200">
                <div>
                  {memberDetails?.id && (
                    <Button
                      onClick={onDeleteInvestorClick}
                      variant="text"
                      sx={{ color: colors.warningRed, minWidth: '125pxs' }}>
                      {translate('company.investorsTable.deleteInvestor')}
                    </Button>
                  )}
                </div>
                <DualButton
                  primaryButtonLabel={translate(
                    activeStep === steps.length - 1
                      ? 'company.addInstituitionalInvestor.save'
                      : 'company.addInstituitionalInvestor.next'
                  )}
                  secondaryButtonVariant="text"
                  secondaryButtonLabel={translate(
                    activeStep === steps.length - 1
                      ? 'company.addInstituitionalInvestor.back'
                      : 'company.addInstituitionalInvestor.cancel'
                  )}
                  secondaryButtonCustomStyle={{
                    color: colors.primaryBlue
                  }}
                  onChangePrimaryButton={onClickPrimaryButton}
                  onChangeSecondaryButton={onClickSecondaryButton}
                  isPrimaryButtonLoading={isLoading || isInvestorUpdating}
                />
              </div>
            </form>
          </div>
        </Paper>
      </div>
    </Modal>
  );
};

export default AddInstituitionalInvestorModal;
